import humps from 'lodash-humps-ts';
import { stringify } from 'qs';
import { array, number, object } from 'yup';

import { API_V1_BASE } from '@config';
import { snakes } from '@utils/lodash';
import { authAxios } from '@components/Auth';
import { CancerQuote, CancerQuoteCompare, CancerQuoteList } from '@models/CancerQuote';
import { CommissionShareParams, QuoteMemberParams, QuoteOptionParams, quoteOptionParamsSchema } from '@models/Quote';
import { NumericString, WithParams } from '@utils/typescript';
import { formatFormData } from '@utils/formik';

export type GetAllCancerQuoteParams = QuoteOptionParams;

export type CompareCancerQuoteParams = QuoteOptionParams & WithParams<{
  cancer_plan_ids: number[];
}>;

export const compareCancerQuoteParamsSchema = quoteOptionParamsSchema.concat(
  object({
    cancerPlanIds: array().of(number())
  })
);

export type FindCancerQuoteParams = QuoteOptionParams & WithParams<{
  cancer_plan_id: number;
}>;

export const findCancerQuoteParamsSchema = quoteOptionParamsSchema.concat(
  object({
    cancerPlanId: number(),
  }),
);

export type SendEmailCancerQuoteParams = FindCancerQuoteParams & {
  name: string;
  email: string;
};

export type CreateCancerQuoteParams = FindCancerQuoteParams & CommissionShareParams & WithParams<{
  me: QuoteMemberParams;
  spouse?: QuoteMemberParams;
  sons?: QuoteMemberParams[];
  daughters?: QuoteMemberParams[];
}>;

export type GetCancerQuoteParams = {
  orderNumber: string;
};

export type UpdateCancerQuoteParams = GetCancerQuoteParams & WithParams<{
  me: QuoteMemberParams;
  spouse?: QuoteMemberParams;
  sons?: QuoteMemberParams[];
  daughters?: QuoteMemberParams[];
}>;

type CustomPremium = WithParams<{
  premium: NumericString;
  discount_premium: NumericString;
}>

export type CustomCancerQuoteParams = GetCancerQuoteParams & WithParams<{
  me: CustomPremium;
  spouse?: CustomPremium;
  sons?: CustomPremium[];
  daughters?: CustomPremium[];
  admin_fee_amount: NumericString;
  note: string;
  proof?: File;
}>;

export class CancerQuoteApi {
  static getAll = async ({ me, spouse, sons, daughters, ...rest }: GetAllCancerQuoteParams) => {
    return await authAxios
      .get(`${API_V1_BASE}/cancer_quotes`, {
        params: snakes({ ...rest, members: { me, spouse, sons, daughters } }),
        paramsSerializer: (parameters) => stringify(parameters, { arrayFormat: 'brackets' }),
      })
      .then((response) => humps(response.data) as CancerQuoteList[]);
  };

  static compare = async ({ me, spouse, sons, daughters, ...rest }: CompareCancerQuoteParams) => {
    return await authAxios
      .get(`${API_V1_BASE}/cancer_quotes`, {
        params: snakes({ ...rest, members: { me, spouse, sons, daughters } }),
        paramsSerializer: (parameters) => stringify(parameters, { arrayFormat: 'brackets' }),
      })
      .then((response) => humps(response.data) as CancerQuoteCompare[]);
  };

  static find = async ({ me, spouse, sons, daughters, ...rest }: FindCancerQuoteParams) => {
    return await authAxios
      .get(`${API_V1_BASE}/cancer_quotes/find`, {
        params: snakes({ ...rest, members: { me, spouse, sons, daughters } }),
        paramsSerializer: (parameters) => stringify(parameters, { arrayFormat: 'brackets' }),
      })
      .then((response) => humps(response.data) as CancerQuote);
  };

  static sendEmail = async ({ me, spouse, sons, daughters, ...rest }: SendEmailCancerQuoteParams) => {
    return await authAxios.post(`${API_V1_BASE}/cancer_quotes/send_email`, snakes({ ...rest, members: { me, spouse, sons, daughters } }));
  };

  static create = async ({ me, spouse, sons, daughters, commissionShares, ...rest }: CreateCancerQuoteParams) => {
    return await authAxios
      .post(`${API_V1_BASE}/user/cancer_quotes`,
        snakes({ ...rest, members: { me, spouse, sons, daughters }, commissionShares }),
      )
      .then((response) => humps(response.data) as CancerQuote);
  };

  static get = async ({ orderNumber }: GetCancerQuoteParams) => {
    return await authAxios
      .get(`${API_V1_BASE}/user/cancer_quotes/${encodeURIComponent(orderNumber)}`)
      .then((response) => humps(response.data) as CancerQuote);
  };

  static update = async ({ orderNumber, me, spouse, sons, daughters, ...rest }: UpdateCancerQuoteParams) => {
    return await authAxios
      .put(`${API_V1_BASE}/user/cancer_quotes/${encodeURIComponent(orderNumber)}`,
        snakes({ ...rest, members: { me, spouse, sons, daughters } }),
      )
      .then((response) => humps(response.data) as CancerQuote);
  };

  static custom = async ({ orderNumber, me, spouse, sons, daughters, ...rest }: CustomCancerQuoteParams) => {
    return await authAxios
      .putForm(`${API_V1_BASE}/user/cancer_quotes/${encodeURIComponent(orderNumber)}/custom`,
        formatFormData(snakes({ ...rest, members: { me, spouse, sons, daughters } })),
      )
      .then((response) => humps(response.data) as CancerQuote);
  };
}
